<template>
  <div>
    <div :class="containerClasses">
      <BaseCallout text="BETA: Experimental feature. Use at your own risk." />

      <div class="mt-6">
        <div class="flex items-center justify-between">
          <div class="flex items-center">
            <base-label id="addHeaderCheckbox"
                        class="text-gray-dark cursor-pointer font-medium"
                        label="Add header to the scraping request?"
                        label-classes="font-medium"
            />
            <checkbox id="addHeaderCheckbox"
                      class="ml-2"
                      :is-checked="isAddHeaderChecked"
                      @update:isChecked="onAddHeaderChecked"
            />
          </div>
          <span class="text-gray text-sm">(optional)</span>
        </div>

        <transition name="slide-fade" mode="out-in">
          <div v-if="isAddHeaderChecked" :key="isAddHeaderChecked" class="mt-2 grid grid-cols-2 gap-2">
            <with-error-message
              v-for="headerField in headerFields"
              :key="headerField.id"
              :show-error="headerField.id === 'headerKey' ? isHeaderKeyError : isHeaderValueError"
              class="col-span-1"
            >
              <base-label :id="headerField.id"
                          :label="headerField.label"
              />
              <base-input :id="headerField.id"
                          :name="headerField.id"
                          :placeholder="headerField.placeholder"
                          variant="editor"
                          type="string"
                          class="w-full border-gray mt-1"
                          :value="headerField.value"
                          @input="headerField.updateMethod"
              />
            </with-error-message>
          </div>
        </transition>
      </div>

      <with-error-message class="relative mt-5" :show-error="isWebPageUrlError">
        <base-label id="url" label="1. Scrape from:" :label-classes="'font-medium'"/>
        <base-input name="url"
                    placeholder="e.g. https://www.website.com/your-html-page"
                    variant="editor"
                    type="string"
                    class="w-full border-gray-border py-px mt-1 h-10"
                    :value="url"
                    @input="updateUrl"
        />
        <whitelist-hint/>
      </with-error-message>

      <BaseCallout
        v-if="isSinglePageAppUrlError"
        :text="getSinglePageAppWorkaroundText()"
        class="mt-3"
        variant="info"
      />

      <div class="mt-5">
        <label class="text-gray-dark font-medium">2. Store as:</label>
        <div class="flex flex-col">
          <dropdown-button name="selectFormatType"
                          primary-button-variant="secondary"
                          primary-button-classes="w-full h-10"
                          dropdown-position="below-left"
                          class="mt-1"
                          arrow-color="gray"
                          :value="format"
                          :options="fileTypeOptions"
                          :close-on-select="true"
                          @select-button="updateFormat"
          />
        </div>
      </div>

      <div class="flex items-center mt-5">
        <base-label id="formatScrapedContentCheckbox"
                    class="text-gray-dark cursor-pointer font-medium"
                    label="3. Additional formatting with GPT?"
                    label-classes="font-medium"
        />
        <checkbox id="formatScrapedContentCheckbox"
                  class="ml-2"
                  :is-checked="isFormattingChecked"
                  @update:isChecked="onFormattingChecked"
        />
      </div>

      <transition name="slide-fade" mode="out-in">
        <div v-if="isFormattingChecked" :key="isFormattingChecked" class="mt-2">
          <base-label id="scrapingPrompt" label="Scraping Prompt" class="ml-2" />
          <t-textarea id="scrapingPrompt"
                      name="scrapingPrompt"
                      placeholder="e.g. Extract the return policy."
                      class="w-full border-gray-border  focus:border-focus mt-1"
                      variant="textSection"
                      rows=5
                      :value="prompt"
                      @input="updatePrompt"
          />
        </div>
      </transition>
    </div>

    <div v-if="formattedContent" :class="{'border-0 border-t border-gray-border border-solid px-8 pt-8': formattedContent }">
      <busy-button id="previewScrapedContentButton"
                   variant="bordered"
                   class="col-span-1"
                   @click="previewScrapedContent"
      >
        <EyeIcon class="h-5 w-5"/>
        Preview scraped content
      </busy-button>
    </div>

    <SettingsIndexesWebPagePreview :is-visible="isPrevieModalVisible"
                                   :formatted-content="formattedContent"
                                   @update:is-visible="isPrevieModalVisible = $event"
    />
  </div>
</template>

<script>
import BusyButton from '@/components/common/BusyButton.vue'
import Checkbox from '@/components/preview/Checkbox'
import DropdownButton from '@/components/common/DropdownButton.vue'
import EyeIcon from '@/assets/images/icons/EyeIcon.vue'
import previewModule from '@/store/modules/preview.module'
import WithErrorMessage from '@/components/common/WithErrorMessage.vue'
import WhitelistHint from '@/components/common/WhitelistHint.vue'
import SettingsIndexesWebPagePreview from '@/components/settings/indexes/SettingsIndexesWebPagePreview.vue'
import DOMPurify from 'dompurify'
import { DEFAULT_EXTRACT_PROMPT } from '@/utils/text.constants'
import dropdownButtonsUtils from '@/utils/dropdown-button.utils'

export default {
  name: 'SettingsIndexesWebPage',
  components: {
    BusyButton,
    Checkbox,
    WithErrorMessage,
    WhitelistHint,
    DropdownButton,
    EyeIcon,
    SettingsIndexesWebPagePreview
  },
  props: {
    url: String,
    prompt: String | null,
    format: String | 'txt',
    headerKey: String | null,
    headerValue: String | null,
    indexSource: Object,
    containerClasses: String
  },
  data() {
    return {
      cachedPrompt: this.prompt || DEFAULT_EXTRACT_PROMPT,
      cachedHeaderKey: this.headerKey || '',
      cachedHeaderValue: this.headerValue || '',
      isPrevieModalVisible: false
    }
  },
  computed: {
    error: previewModule.error,
    errorDescription: previewModule.errorDescription,

    isFormattingChecked() {
      return this.prompt !== null && this.prompt !== ''
    },

    isAddHeaderChecked() {
      return this.headerKey !== null && this.headerValue !== null
    },

    isHeaderKeyError() {
      return this.errorDescription?.includes('Header Key')
    },

    isHeaderValueError() {
      return this.errorDescription?.includes('Header Value')
    },

    isWebPageUrlError() {
      return this.errorDescription?.includes('URL') || (typeof this.error === 'string' && this.error?.includes('Supported'))
    },

    isSinglePageAppUrlError() {
      return this.errorDescription?.includes('JavaScript-rendered')
    },

    scrapedContent() {
      return this.indexSource?.config?.content
    },

    formattedContent() {
      if (!this.scrapedContent) return null
      
      // Check if content is already HTML or markdown
      const isHtml = /<[^>]*>/g.test(this.scrapedContent)
      
      if (isHtml) {
        // Content is already HTML, just sanitize it
        return DOMPurify.sanitize(this.scrapedContent)
      } else {
        // Content is likely markdown, convert it to HTML
        // Note: You'll need to install a markdown library like 'marked'
        // For now, we'll just sanitize the plain text
        return DOMPurify.sanitize(this.scrapedContent.replace(/\n/g, '<br>'))
      }
    },

    fileTypeOptions() {
      return dropdownButtonsUtils.getScrapedFileTypeOptions()
    },

    headerFields() {
      return [
        {
          id: 'headerKey',
          label: 'Key',
          placeholder: 'e.g. your-header-key',
          value: this.headerKey,
          updateMethod: this.updateHeaderKey
        },
        {
          id: 'headerValue',
          label: 'Value',
          placeholder: 'e.g. your-header-value',
          value: this.headerValue,
          updateMethod: this.updateHeaderValue
        }
      ]
    }
  },
  methods: {
    updateUrl(updatedUrl) {
      this.$emit('update:url', updatedUrl)
      if (this.isWebPageUrlError) this.error = null
    },
    updatePrompt(updatedPrompt) {
      this.cachedPrompt = updatedPrompt
      this.$emit('update:prompt', updatedPrompt)
    },
    updateFormat(index) {
      const updatedFormat = this.fileTypeOptions[index].value
      this.$emit('update:format', updatedFormat)
    },
    updateHeaderKey(updatedHeaderKey) {
      this.cachedHeaderKey = updatedHeaderKey
      this.$emit('update:headerKey', updatedHeaderKey)
      if (this.isHeaderKeyError) this.error = null
    },
    updateHeaderValue(updatedHeaderValue) {
      this.cachedHeaderValue = updatedHeaderValue
      this.$emit('update:headerValue', updatedHeaderValue)
      if (this.isHeaderValueError) this.error = null
    },
    onFormattingChecked(isFormattingChecked) {
      this.$emit('update:prompt', isFormattingChecked ? this.cachedPrompt : null)
    },
    onAddHeaderChecked(isAddHeaderChecked) {
      this.$emit('onAddHeaderChecked', {
        headerKey: isAddHeaderChecked ? this.cachedHeaderKey : null,
        headerValue: isAddHeaderChecked ? this.cachedHeaderValue : null
      })
    },
    previewScrapedContent() {
      this.isPrevieModalVisible = true
    },
    getSinglePageAppWorkaroundText() {
      return `⚠️ ${this.errorDescription}
      <br/><br/>
      Try this instead:<br/>
      1. Load the page in browser<br/>
      2. Wait for JS to render<br/>
      3. Save the page as HTML<br/>
      4. Add the HTML file to the knowledge base
      <br/><br/>
      Note: These steps will have to be repeated if the page info is updated.
      `
    }
  },
  destroyed() {
    if (this.isWebPageUrlError) this.error = null
  }
}
</script>
